export const GRID_MIN = 1;
export const GRID_MAX = 5;

/**
 * Wandelt eine Pixelposition innerhalb einer Achse (0..size) in einen auf
 * 1-5 gerasterten Wert um. Robust gegen fehlende/ungueltige Groessen (z. B.
 * getBoundingClientRect() vor dem ersten Layout) — faellt dann auf GRID_MIN
 * zurueck statt NaN zu produzieren.
 */
export function pixelToLevel(pixel: number, size: number): number {
  if (!size || !isFinite(size) || size <= 0) return GRID_MIN;
  const ratio = Math.min(1, Math.max(0, pixel / size));
  const raw = GRID_MIN + ratio * (GRID_MAX - GRID_MIN);
  return Math.min(GRID_MAX, Math.max(GRID_MIN, Math.round(raw)));
}

/** Kehrfunktion zu pixelToLevel — Rasterwert (1-5) auf Pixelposition (0..size). */
export function levelToPixel(level: number, size: number): number {
  const clamped = Math.min(GRID_MAX, Math.max(GRID_MIN, level));
  return ((clamped - GRID_MIN) / (GRID_MAX - GRID_MIN)) * size;
}

export interface JitterPoint {
  id: string;
  power: number;
  interest: number;
}

export interface JitterOffset {
  dx: number;
  dy: number;
}

/**
 * Stakeholder mit identischem Power/Interest-Paar liegen sonst exakt
 * uebereinander. Gruppiert Punkte nach Zelle und verteilt sie kreisfoermig
 * um den Zellmittelpunkt, damit alle sichtbar bleiben.
 */
export function computeJitter(points: JitterPoint[], radius = 9): Map<string, JitterOffset> {
  const groups = new Map<string, JitterPoint[]>();
  for (const p of points) {
    const key = `${p.power}x${p.interest}`;
    const arr = groups.get(key);
    if (arr) arr.push(p); else groups.set(key, [p]);
  }
  const result = new Map<string, JitterOffset>();
  for (const arr of groups.values()) {
    const n = arr.length;
    if (n === 1) {
      result.set(arr[0]!.id, { dx: 0, dy: 0 });
      continue;
    }
    arr.forEach((p, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      result.set(p.id, { dx: Math.cos(angle) * radius, dy: Math.sin(angle) * radius });
    });
  }
  return result;
}
